import React from 'react';
import {
  Button,
  Form,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
  PositionUtil,
  Textarea,
  type ApiError,
} from '@axiom/ui';
import { CandidateOpportunitiesConst } from '@axiom/const';
import type {
  Opportunity,
  OpportunityCandidateOrMlRecommended,
  Candidate,
  CandidateRedux,
} from '@axiom/validation';
import { TalentWarmingModalSchema } from '@axiom/validation';

import { CandidateOpportunityLegacyApi } from '../../api/candidate-opportunity-legacy-api';
import { OpportunityLegacyApi } from '../../api/opportunity-legacy-api';
import { PreloadedAppErrorsStore } from '../../stores/preloaded-app-errors-store';
import { TalentActivitiesApi } from '../../api/talent-activities-api';

const { CandidateStatuses } = CandidateOpportunitiesConst;

export type OpportunityTalentWarmingModalProps = {
  onClose: () => void;
  opportunity: Opportunity;
  opportunityCandidate: OpportunityCandidateOrMlRecommended | undefined;
  candidate: CandidateRedux | Candidate;
};

const buildWarmingMessage = ({
  opportunity,
  position,
}: {
  opportunity: Opportunity;
  position: NonNullable<OpportunityCandidateOrMlRecommended['position']> | null;
}) =>
  `Time-sensitive: We think you'd be a great match for this opportunity. Axiom is preparing to submit talent to ${opportunity.account?.calculatedName ?? '--'} in the next 24 hours - please review the details and let us know if we can share your profile.\n\n` +
  `Estimated Start Date: ${position?.startDate ?? '--'}\n` +
  `Estimated Duration: ${PositionUtil.getEngagementLengthGrouped(position) ?? '--'}\n` +
  `Weekly Commitment: ${position?.billingHoursPerWeek ?? 40} hrs\n` +
  // No trailing newline: the editor renders one as an empty final line.
  `Work Location: ${PositionUtil.getWorksite(position) ?? '--'}${
    opportunity.ownerUser?.fullName
      ? `\nHave questions? Contact ${opportunity.ownerUser.fullName}`
      : ''
  }`;

export const OpportunityTalentWarmingModal = ({
  onClose,
  opportunity,
  opportunityCandidate,
  candidate,
}: OpportunityTalentWarmingModalProps) => {
  const opportunityId = opportunity.id;
  const candidateId = candidate.id;
  const position = opportunityCandidate?.position ?? null;

  const initialValues = {
    candidateStatusMessage: buildWarmingMessage({ opportunity, position }),
  };

  return (
    <Form
      name="WARMING_MODAL_FORM"
      schema={TalentWarmingModalSchema}
      initialValues={initialValues}
      onSubmit={async changedData => {
        try {
          await OpportunityLegacyApi.updateOpportunityCandidate(
            opportunityId,
            candidateId,
            {
              candidateStatus: CandidateStatuses.Warmed,
              candidateStatusMessage:
                changedData.candidateStatusMessage ||
                initialValues.candidateStatusMessage,
            }
          );
        } catch (e) {
          PreloadedAppErrorsStore.show(
            ((e as ApiError).getErrors() as { message: string } | undefined)
              ?.message
          );
        }
        await Promise.all([
          OpportunityLegacyApi.refreshOpportunityCandidates(opportunityId),
          OpportunityLegacyApi.refreshOpportunityPositions(opportunityId),
          CandidateOpportunityLegacyApi.refreshCandidateOpportunities(
            candidateId
          ),
          TalentActivitiesApi.refreshActivities(candidateId),
        ]);
        onClose();
      }}
    >
      {({ fireSubmit }) => (
        <Modal name="WARMING_MODAL">
          <ModalHeader
            name="WARMING_MODAL_HEADER"
            background="blue"
            onClose={() => {
              onClose();
            }}
          >
            Warm Talent
          </ModalHeader>

          <ModalSection>
            <Textarea
              name="candidateStatusMessage"
              label="Talent will be notified immediately, and respond in AFT."
              formatting="modern"
            />
          </ModalSection>
          <ModalFooter>
            <Button
              className="warming-talent-action"
              onClick={() => {
                onClose();
              }}
              name="WARMING_MODAL_CANCEL_BUTTON"
              variation="outline"
            >
              Cancel
            </Button>
            <Button
              name="WARMING_MODAL_SAVE_BUTTON"
              className="warming-talent-action"
              onClick={fireSubmit}
            >
              Send
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </Form>
  );
};
